import { isUnitType, readDensityLimits, sortBands, type DensityLimitBand, type UnitType } from './density'

// Capacidad de carga (findings #151). The farm's set densities and minimum
// water flow as named tables per facility (like the SFR / mortality tables),
// because the limits follow the genetics: each supplier delivers several
// strains / broodstock groups, and they change every 2–3 years. A lot of an
// arrival keeps a copy of the table it was assigned, so later edits never
// rewrite what applied in the past.
//
// - `Farm.metadata.carryingCapacityTables`     CarryingCapacityTable[]
// - `Farm.metadata.carryingCapacityDefaultId`  the facility default
// - `Farm.metadata.densityLimits`              kept = the default's kg/m³ bands
//                                              (read by older open tabs, #96)
// - `Lot.metadata.carryingCapacity`            LotCapacityAssignment (the copy)
//
// No migration. GLOBALG.A.P. IFA AQ 20.02.13 (densities by size, stage,
// environment and system; never an average; never exceeded; calculated and
// recorded); ASC Farm Standard 4.1.1.12 + App. 12.5.4 (density at the highest
// stocked volume / count) and 4.1.1.10.5 (green / amber / red); BAP Farm 3.1
// (water quality per unit by stocking density). Flow: Lekang, Aquaculture
// Engineering ch. 10–11 (water need = O₂ use ÷ (O₂ in − O₂ out), by size and
// temperature); FONDEPES trout manual (1 L/min per 1,000 eyed eggs).

/** One weight band: kg/m³ per unit type and the minimum flow per temperature band. */
export type CapacityBand = DensityLimitBand & {
  /** L/min per kg, one per temperature band (`flowTemperaturesC` + the open band); null = no minimum. */
  minFlowLpmPerKg: Array<number | null>
}

export type CarryingCapacityTable = {
  id: string
  name: string
  /** Sorted by weight; the last band is open-ended (`maxWeightG: null`). */
  bands: CapacityBand[]
  /** Upper bounds (°C) of the temperature bands, ascending; one more open band follows. */
  flowTemperaturesC: number[]
  /** Hatchery (eggs / yolk-sac alevins): minimum L/min per 1,000. */
  hatcheryMinLpmPerThousand: number | null
  updatedAt: string
}

export type LotCapacityAssignment = {
  tableId: string
  tableName: string
  /** When the copy was taken. */
  assignedAt: string
  table: CarryingCapacityTable
}

export type ResolvedCapacity = {
  table: CarryingCapacityTable
  /** 'lot' = the lot's (or its arrival's) copy; 'facility' = the facility default. */
  source: 'lot' | 'facility'
  assignedAt: string | null
}

export const LEGACY_DEFAULT_TABLE_ID = 'facility-default'
/** Amber from 90 % of a maximum (ASC traffic light); for a minimum, below minimum ÷ 0.9. */
export const AMBER_FRACTION = 0.9

type WithMetadata = { metadata?: Record<string, unknown> | null } | null | undefined

const positive = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : Number.NaN
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

const finite = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : Number.NaN
  return Number.isFinite(parsed) ? parsed : null
}

const readTemperatures = (raw: unknown): number[] => {
  if (!Array.isArray(raw)) return []
  const values = raw.map(finite).filter((value): value is number => value !== null)
  return [...new Set(values)].sort((a, b) => a - b)
}

const readFlowRow = (raw: unknown, columns: number): Array<number | null> => {
  const values = Array.isArray(raw) ? raw : []
  return Array.from({ length: columns }, (_, index) => positive(values[index]))
}

export const normalizeCapacityTable = (raw: unknown): CarryingCapacityTable | null => {
  if (!raw || typeof raw !== 'object') return null
  const record = raw as Record<string, unknown>
  const id = typeof record.id === 'string' ? record.id.trim() : ''
  const name = typeof record.name === 'string' ? record.name.trim() : ''
  if (!id || !name) return null
  const flowTemperaturesC = readTemperatures(record.flowTemperaturesC)
  const columns = flowTemperaturesC.length + 1
  const rawBands = Array.isArray(record.bands) ? record.bands : []
  const densityBands = readDensityLimits({ metadata: { densityLimits: rawBands } })
  // readDensityLimits drops invalid entries and sorts: pair each kept band with its raw flow row.
  const flowByWeight = new Map<string, unknown>()
  for (const entry of rawBands) {
    if (!entry || typeof entry !== 'object') continue
    const band = entry as Record<string, unknown>
    const key = band.maxWeightG === null || band.maxWeightG === undefined ? 'open' : String(positive(band.maxWeightG))
    flowByWeight.set(key, band.minFlowLpmPerKg)
  }
  const bands: CapacityBand[] = densityBands.map((band) => ({
    ...band,
    minFlowLpmPerKg: readFlowRow(flowByWeight.get(band.maxWeightG === null ? 'open' : String(band.maxWeightG)), columns)
  }))
  if (!bands.some((band) => band.maxWeightG === null)) {
    bands.push({ maxWeightG: null, limits: {}, minFlowLpmPerKg: readFlowRow([], columns) })
  }
  return {
    id,
    name,
    bands,
    flowTemperaturesC,
    hatcheryMinLpmPerThousand: positive(record.hatcheryMinLpmPerThousand),
    updatedAt: typeof record.updatedAt === 'string' ? record.updatedAt : new Date(0).toISOString()
  }
}

const legacyTable = (farm: WithMetadata): CarryingCapacityTable | null => {
  const bands = readDensityLimits(farm)
  if (bands.length === 0) return null
  return normalizeCapacityTable({ id: LEGACY_DEFAULT_TABLE_ID, name: 'Predeterminada', bands, flowTemperaturesC: [] })
}

/** The facility's tables, sorted by name. Before any table exists, the #96 limits read as one default table. */
export const readCapacityTables = (farm: WithMetadata): CarryingCapacityTable[] => {
  const raw = farm?.metadata?.carryingCapacityTables
  const tables = Array.isArray(raw)
    ? raw.map(normalizeCapacityTable).filter((table): table is CarryingCapacityTable => table !== null)
    : []
  if (tables.length === 0) {
    const legacy = legacyTable(farm)
    return legacy ? [legacy] : []
  }
  return tables.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
}

export const readDefaultCapacityTable = (farm: WithMetadata): CarryingCapacityTable | null => {
  const tables = readCapacityTables(farm)
  const defaultId = farm?.metadata?.carryingCapacityDefaultId
  return tables.find((table) => table.id === defaultId) ?? (tables.length === 1 ? tables[0] : null)
}

/** Farm metadata with these tables and default; `densityLimits` mirrors the default's kg/m³ bands. */
export const withCapacityTables = <T extends NonNullable<WithMetadata>>(
  farm: T,
  tables: CarryingCapacityTable[],
  defaultId: string | null
): T => {
  const metadata: Record<string, unknown> = { ...(farm.metadata ?? {}) }
  metadata.carryingCapacityTables = tables
  const resolvedDefault = tables.find((table) => table.id === defaultId) ?? null
  if (resolvedDefault) metadata.carryingCapacityDefaultId = resolvedDefault.id
  else delete metadata.carryingCapacityDefaultId
  const mirror = resolvedDefault?.bands.map((band) => ({ maxWeightG: band.maxWeightG, limits: band.limits })) ?? []
  if (mirror.some((band) => Object.keys(band.limits).length > 0)) metadata.densityLimits = mirror
  else delete metadata.densityLimits
  return { ...farm, metadata }
}

export const readLotCapacityAssignment = (lot: WithMetadata): LotCapacityAssignment | null => {
  const raw = lot?.metadata?.carryingCapacity
  if (!raw || typeof raw !== 'object') return null
  const record = raw as Record<string, unknown>
  const table = normalizeCapacityTable(record.table)
  if (!table) return null
  return {
    tableId: typeof record.tableId === 'string' && record.tableId ? record.tableId : table.id,
    tableName: typeof record.tableName === 'string' && record.tableName ? record.tableName : table.name,
    assignedAt: typeof record.assignedAt === 'string' ? record.assignedAt : table.updatedAt,
    table
  }
}

export const capacityAssignmentFor = (table: CarryingCapacityTable, assignedAt: string): LotCapacityAssignment => ({
  tableId: table.id,
  tableName: table.name,
  assignedAt,
  table: JSON.parse(JSON.stringify(table)) as CarryingCapacityTable
})

export const withLotCapacityAssignment = <T extends NonNullable<WithMetadata>>(lot: T, assignment: LotCapacityAssignment | null): T => {
  const metadata: Record<string, unknown> = { ...(lot.metadata ?? {}) }
  if (assignment) metadata.carryingCapacity = assignment
  else delete metadata.carryingCapacity
  return { ...lot, metadata }
}

type LotRef = { id: string; lineageRootId?: string | null; metadata?: Record<string, unknown> | null }

/**
 * The lot's copy; else the latest copy in its lineage (the arrival's other
 * unit lots, or the lot it was split from); else the facility default.
 */
export const resolveLotCapacity = (
  farm: WithMetadata,
  lot: LotRef | null | undefined,
  lotsById?: ReadonlyMap<string, LotRef>
): ResolvedCapacity | null => {
  const own = readLotCapacityAssignment(lot)
  if (own) return { table: own.table, source: 'lot', assignedAt: own.assignedAt }
  const rootId = lot ? lot.lineageRootId ?? lot.id : null
  if (rootId && lotsById) {
    let latest: LotCapacityAssignment | null = null
    for (const candidate of lotsById.values()) {
      if (candidate.id !== rootId && (candidate.lineageRootId ?? candidate.id) !== rootId) continue
      const assignment = readLotCapacityAssignment(candidate)
      if (assignment && (!latest || assignment.assignedAt > latest.assignedAt)) latest = assignment
    }
    if (latest) return { table: latest.table, source: 'lot', assignedAt: latest.assignedAt }
  }
  const fallback = readDefaultCapacityTable(farm)
  return fallback ? { table: fallback, source: 'facility', assignedAt: null } : null
}

const bandFor = (table: CarryingCapacityTable, averageWeightG: number | null | undefined): CapacityBand | null => {
  if (typeof averageWeightG !== 'number' || !Number.isFinite(averageWeightG) || averageWeightG <= 0) return null
  return (sortBands(table.bands) as CapacityBand[])
    .find((candidate) => candidate.maxWeightG === null || averageWeightG <= candidate.maxWeightG) ?? null
}

export const capacityDensityLimit = (
  table: CarryingCapacityTable | null | undefined,
  unitType: unknown,
  averageWeightG: number | null | undefined
): number | null => {
  if (!table || !isUnitType(unitType)) return null
  return bandFor(table, averageWeightG)?.limits[unitType as UnitType] ?? null
}

/** Minimum L/min per kg for this weight and water temperature, or null. */
export const capacityMinFlowPerKg = (
  table: CarryingCapacityTable | null | undefined,
  averageWeightG: number | null | undefined,
  temperatureC: number | null | undefined
): number | null => {
  if (!table || typeof temperatureC !== 'number' || !Number.isFinite(temperatureC)) return null
  const band = bandFor(table, averageWeightG)
  if (!band) return null
  const column = table.flowTemperaturesC.findIndex((upper) => temperatureC <= upper)
  return band.minFlowLpmPerKg[column === -1 ? table.flowTemperaturesC.length : column] ?? null
}

export type CapacityStatus = 'ok' | 'amber' | 'red'

/** Against a maximum (kg/m³): red over it, amber from 90 %. */
export const statusAgainstMaximum = (value: number | null | undefined, maximum: number | null | undefined): CapacityStatus | null => {
  if (typeof value !== 'number' || !Number.isFinite(value) || typeof maximum !== 'number' || !(maximum > 0)) return null
  if (value > maximum) return 'red'
  if (value >= maximum * AMBER_FRACTION) return 'amber'
  return 'ok'
}

/** Against a minimum (flow): red under it, amber under minimum ÷ 0.9. */
export const statusAgainstMinimum = (value: number | null | undefined, minimum: number | null | undefined): CapacityStatus | null => {
  if (typeof value !== 'number' || !Number.isFinite(value) || typeof minimum !== 'number' || !(minimum > 0)) return null
  if (value < minimum) return 'red'
  if (value < minimum / AMBER_FRACTION) return 'amber'
  return 'ok'
}

/**
 * Findings #163: a word next to the colour, so red and amber never rely on
 * colour alone. `kind` is what the value is checked against.
 */
export const CAPACITY_STATUS_WORD_KEY = {
  maximum: { red: 'capacity.status.overLimit', amber: 'capacity.status.nearLimit' },
  minimum: { red: 'capacity.status.underMinimum', amber: 'capacity.status.nearMinimum' }
} as const

export const capacityStatusWordKey = (status: CapacityStatus | null | undefined, kind: 'maximum' | 'minimum') => (
  status === 'red' || status === 'amber' ? CAPACITY_STATUS_WORD_KEY[kind][status] : null
)

export const CAPACITY_STATUS_COLOR: Record<CapacityStatus, string> = {
  ok: '#15803d',
  // Findings #163: a brighter orange, clearly apart from the red.
  amber: '#ea580c',
  red: '#b91c1c'
}

export const emptyCapacityBand = (temperatureBands: number): CapacityBand => ({
  maxWeightG: null,
  limits: {},
  minFlowLpmPerKg: Array.from({ length: temperatureBands }, () => null)
})


export type CapacityCheckInput = {
  table: CarryingCapacityTable | null
  unitType: unknown
  /** Useful water volume, m³ (null = not set). */
  volumeM3: number | null
  /** Eggs / yolk-sac alevins: checked per 1,000, not per kg. */
  hatchery: boolean
  /** Fish (or eggs) in the unit now. */
  stock: number | null
  averageWeightG: number | null
  temperatureC: number | null
  flowLpm: number | null
}

export type CapacityCheck = {
  biomassKg: number | null
  densityKgM3: number | null
  densityLimitKgM3: number | null
  densityStatus: CapacityStatus | null
  flowLpmPerKg: number | null
  minFlowLpmPerKg: number | null
  flowStatus: CapacityStatus | null
  flowPerThousandLpm: number | null
  hatcheryMinLpmPerThousand: number | null
  hatcheryFlowStatus: CapacityStatus | null
}

/** Today's density and flow against the lot's table (findings #151). Pure. */
export const buildCapacityCheck = (input: CapacityCheckInput): CapacityCheck => {
  const stock = typeof input.stock === 'number' && input.stock > 0 ? input.stock : null
  const flow = typeof input.flowLpm === 'number' && input.flowLpm > 0 ? input.flowLpm : null
  if (input.hatchery) {
    const perThousand = stock !== null && flow !== null ? flow / (stock / 1000) : null
    const minimum = input.table?.hatcheryMinLpmPerThousand ?? null
    return {
      biomassKg: null,
      densityKgM3: null,
      densityLimitKgM3: null,
      densityStatus: null,
      flowLpmPerKg: null,
      minFlowLpmPerKg: null,
      flowStatus: null,
      flowPerThousandLpm: perThousand,
      hatcheryMinLpmPerThousand: minimum,
      hatcheryFlowStatus: statusAgainstMinimum(perThousand, minimum)
    }
  }
  const weight = typeof input.averageWeightG === 'number' && input.averageWeightG > 0 ? input.averageWeightG : null
  const biomassKg = stock !== null && weight !== null ? (stock * weight) / 1000 : null
  const densityKgM3 = biomassKg !== null && input.volumeM3 !== null && input.volumeM3 > 0 ? biomassKg / input.volumeM3 : null
  const densityLimitKgM3 = capacityDensityLimit(input.table, input.unitType, weight)
  const flowLpmPerKg = biomassKg !== null && biomassKg > 0 && flow !== null ? flow / biomassKg : null
  const minFlowLpmPerKg = capacityMinFlowPerKg(input.table, weight, input.temperatureC)
  return {
    biomassKg,
    densityKgM3,
    densityLimitKgM3,
    densityStatus: statusAgainstMaximum(densityKgM3, densityLimitKgM3),
    flowLpmPerKg,
    minFlowLpmPerKg,
    flowStatus: statusAgainstMinimum(flowLpmPerKg, minFlowLpmPerKg),
    flowPerThousandLpm: null,
    hatcheryMinLpmPerThousand: null,
    hatcheryFlowStatus: null
  }
}
